import { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, X, MagnifyingGlass } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { shizaiApi } from '../shizaiApi';

let _ddCache = null;

/** 納入先ピッカー — 納入先マスター（有効のみ）から検索して1件選択。 */
export function DeliveryDestPicker({ value, onChange, testId = 'pr-delivery-dest' }) {
  const [list, setList] = useState(_ddCache || []);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (_ddCache) return;
    shizaiApi.deliveryDestinations()
      .then((rows) => { _ddCache = rows; setList(rows); })
      .catch(() => setList([]));
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    const rows = !s ? list : list.filter((d) =>
      [d.code, d.name, d.formal_name, d.kana, d.department]
        .filter(Boolean).join(' ').toLowerCase().includes(s));
    return rows.slice(0, 50);
  }, [list, q]);

  if (value) {
    return (
      <div className="mt-1">
        <div className="flex items-center gap-2 rounded-md border border-teal-200 bg-teal-50/60 px-2.5 py-1.5"
          data-testid={`${testId}-selected`}>
          <MapPin size={14} weight="duotone" className="text-teal-600 shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-semibold text-zinc-800 truncate">
              <span className="font-mono text-[10px] text-zinc-400 mr-1.5">{value.code}</span>
              {value.name}
            </span>
            {value.formal_name && value.formal_name !== value.name && (
              <span className="block text-[10px] text-zinc-500 truncate">{value.formal_name}</span>
            )}
          </span>
          <button type="button" onClick={() => onChange(null)}
            className="text-zinc-400 hover:text-rose-600 shrink-0" data-testid={`${testId}-clear`}>
            <X size={13} weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mt-1" ref={wrapRef}>
      <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
      <Input value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="納入先を検索（コード / 名称 / カナ）..."
        className="h-9 pl-8 text-sm" data-testid={`${testId}-search`} />
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-zinc-200 bg-white shadow-lg"
          data-testid={`${testId}-dropdown`}>
          {matches.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-zinc-400">該当する納入先がありません</p>
          ) : matches.map((d) => (
            <button key={d.id} type="button"
              onClick={() => { onChange({ id: d.id, code: d.code, name: d.name, formal_name: d.formal_name }); setQ(''); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 hover:bg-teal-50 transition-colors border-b border-zinc-50 last:border-b-0"
              data-testid={`${testId}-opt-${d.id}`}>
              <span className="block text-xs text-zinc-800 truncate">
                <span className="font-mono text-[10px] text-zinc-400 mr-1.5">{d.code}</span>
                <span className="font-medium">{d.name}</span>
              </span>
              {d.formal_name && d.formal_name !== d.name && (
                <span className="block text-[10px] text-zinc-400 truncate">{d.formal_name}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
